import { VoidCallback1 } from '@deqo/foundation';
import React from 'react';
import styled, { css } from 'styled-components';
import { LFC } from '../../Legacy';
import { Icon } from '../../concepts/resources/Icon';
import { BetterDropButton, DropButton } from '../../ui/DropButton';
import { Colors, toRem } from './../../ui';

type PopupBaseProps = {
    w?: string | number,
    h?: string | number,
    p?: string | number,
    maximize?: boolean,
    shadow?: 'off' | 'md' | 'lg',
    background?: string,
};

const Base = styled.div<PopupBaseProps>`
    position: relative;
    display: flex;
    flex-direction: column;
    ${p => (p.w || p.maximize) ? `width: ${p.maximize || p.w === 'full' ? '100%' : toRem(p.w)}` : ''};
    ${p => (p.h || p.maximize) ? css`
        max-height: ${p.maximize || p.h === 'full' ? '100%' : toRem(p.h)};
        height: 100%;
    ` : css`
        max-height: 100%;
    `}
    padding: ${p => toRem(p.p ?? 1.5)};
    border-radius: ${p => (p.h === 'full' || p.maximize) ? '0' : '0.5rem'};
    background-color: ${p => p.background ?? '#fff'};
    box-shadow: 
        0 1.25rem 1.5rem -.375rem rgba(0,0,0,0.1), 
        0 0.625rem 0.625rem -.375rem rgba(0,0,0,0.04);
    color: #444;
    /* overflow: hidden; */

    ${p => p.shadow === 'off' ? css`
        box-shadow: none;
    ` : ''}

    ${p => p.shadow === 'lg' ? css`
        box-shadow: 
            0rem 0rem 0.1rem 0rem rgb(0 0 0 / 5%), 
            0rem 0rem 10rem 3rem rgb(0 0 0 / 5%);
            /* 0 .675rem 3.5rem 0rem rgba(0,0,0,0.2), 
            0 0.625rem 0.625rem 0rem rgba(0,0,0,0.1); */
    ` : ''}

    transition: 
        width 400ms cubic-bezier(0,.99,.24,1),
        height 400ms cubic-bezier(0,.99,.24,1);
`;

const Title = styled.div<{ center?: boolean }>`
    flex: 0 0 auto;
    margin-bottom: .75rem;
    font-size: 1.5rem;
    font-weight: 450;
    color: ${Colors.text};
    text-align: ${p => p.center ? 'center' : 'left'};
`;

const SubTitle = styled.div`
    font-weight: 450;
    color: ${Colors.textLight};
`;

const IconFrame = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3.5rem;
    height: 3.5rem;
    margin-right: 1.25rem;
    background: ${Colors.hover};
    border-radius: .5rem;
    color: ${Colors.primary};
`;

const Header = styled.div`
    display: grid;
    grid-template-columns: min-content 1fr;
    grid-template-rows: min-content min-content;

    ${IconFrame} {
        grid-row: 1 / 3;
    }
    ${Title} { 
        grid-column: 2;
        margin: 0;
        color: ${Colors.textLight};
     }
    ${SubTitle} { 
        grid-column: 2;
        color: ${Colors.textLighter};
     }
`;

const Content = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    /* overflow: auto; */
`;

const Actions = styled.div`
    position: relative;
    flex: 0 0 auto;
    margin-top: .75rem;
    display: flex;
    align-items: center;
    justify-content: flex-end;

    & > * {
        margin-left: .5rem;
    }
`;

const ActionPull = styled.div`
    margin-left: 0;
    margin-right: auto;
`;

const CloseMount = styled.div<{ position?: any }>`
    position: ${p => p.position || 'absolute'};
    right: 1rem;
    top: 1rem;

    ${DropButton} {
        color: #444 !important;
        &:hover { color: #444 !important; }
        &:active { color: #444 !important; }
    }
`;

const Close: LFC<{ position?: any, onAction: any }> = ({ position, onAction }) =>
    <CloseMount position={position} onClick={onAction}>
        <DropButton>
            <Icon src="deqo://icons/hero/x" />
        </DropButton>
    </CloseMount>;

const BackMount = styled.div`
    display: inline-block;
    position: relative;
    top: 0.1875rem;
    margin: 0 0.25rem 0 -0.25rem;
`;

const Back: LFC<{ onAction: any }> = ({ onAction }) =>
    <BackMount onClick={onAction}>
        <BetterDropButton padding=".375" overflow="0">
            <Icon size="1.5" src="deqo://icons/hero/arrow-left" />
        </BetterDropButton>
    </BackMount>;

const MinMaxMount = styled.div`
    position: absolute;
    right: 4rem;
    top: 1rem;

    ${DropButton} {
        color: #444 !important;
        &:hover { color: #444 !important; }
        &:active { color: #444 !important; }
    }
`;

const MinMax: LFC<{ value: boolean, onAction: VoidCallback1<boolean> }> = ({ value, onAction }) =>
    <MinMaxMount onClick={() => onAction(!value)}>
        <DropButton>
            <Icon src={`deqo://icons/hero/arrow-${value ? 'down' : 'up'}@[1,1,45]`} />
        </DropButton>
    </MinMaxMount>;

export const Popup = Object.assign(Base, {
    Base,
    Header,
    Title,
    SubTitle,
    IconFrame,
    Content,
    Back,
    Close,
    MinMax,
    Actions,
    ActionPull
});

export type PopupAction = (data: any) => void;
export type PopupDismiss = () => void;
export type PopupProps<T = Record<string, any>> = { action: PopupAction, dismiss: PopupDismiss, args: T };

export interface PopupDefinition {
    ui: React.ComponentType<PopupProps>;
    droppable?: boolean;
    fullscreen?: boolean;
    fade?: boolean;
    args?: Record<string, any>;
}

export interface PopupInstance {
    id: string;
    ui: React.ComponentType;
    droppable: boolean;
    fullscreen: boolean;
    fade: boolean;
    args: Record<string, any>;
    action: (data: any) => void;
    dismiss: () => void;
}

export interface PopupResult {
    actioned: boolean;
    data?: any;
}
